// ImageDisplay.js
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, addYears, subYears } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons'; // faComment, faComments removed

import './css/controls.css';
import './css/dialog.css';
import './css/dateStrap.css';

import FeedbackDialog from './components/FeedbackDialog';
import DebugDisplay from './components/DebugDisplay';
import AboutDialog from './components/AboutDialog';
import NoteDialog from './components/NoteDialog';
import PrivacyDialog from './components/PrivacyDialog';
import Alert from './components/Alert';
import Clipboard from './components/Clipboard';
import DateStrap from './components/DateStrap';
import CalendarDialog from './components/Calendar';
import TreeView from './components/TreeView';
import CalcMoment from './components/CalcMoment';
import ImageTransform from './components/ImageTransform';
import { renderDateControls, renderSpecialControls } from './components/Controls';
import useDeviceDetect from './components/UseDeviceDetect';

import config from './config/config.json';

const ALERT_TIMEOUT = 5000;
const NEW_BADGE_TIMEOUT = 7000;

const ImageDisplay = ( apiAuthKey ) => {
  const debug = process.env.REACT_APP_DEBUG_MODE;

  const imageViewerRef = useRef(null);

  const [imageUrl, setImageUrl] = useState('');
  const [loaded, setLoaded] = useState(false);

  const { isMobileView, isLandscapeView, deviceType } = useDeviceDetect();
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [showDatesDialog, setShowDatesDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [copied, setCopied] = useState(false);

  const [showNew, setShowNew] = useState(config.isNewVisible);

  const [menuVisible, setMenuVisible] = useState(false);
  const [dayMenuVisible, setDayMenuVisible] = useState(true);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [isNote, setIsNote] = useState(false);

  // Removed smallDateStrap state and its useEffect; use isMobileView directly

  const isValidDate = useCallback((dateString, cfg) => {
    if (!dateString || dateString.length !== 10) return false; // Check format YYYY-MM-DD
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) &&
      date >= new Date(cfg.lowerDateBound) && date <= new Date(cfg.upperDateBound);
  }, []); // Assuming cfg structure from config.json is stable or use specific keys if needed

  const { userDate: paramDate } = useParams();
  const [searchParams] = useSearchParams();
  const queryDate = searchParams.get('date');

  const initialDate = useMemo(() => {
    let dateToUse = queryDate || paramDate || config.defaultDate;
    if (!isValidDate(dateToUse, config)) {
      dateToUse = config.defaultDate;
    }
    return dateToUse;
  }, [queryDate, paramDate, isValidDate]); // config is stable, not needed in deps

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const navigate = useNavigate();

  const handleEvent = useCallback(async (action) => {
    if (config.enableEvents) {
      try {
        const width = window.innerWidth;
        const height = window.innerHeight;
        await fetch(`${config.apiEventUrl}`, {
          method: 'POST',
          headers: {
            'Authorization': `${apiAuthKey.apiAuthKey}`
          },
          body: JSON.stringify({
            "action": action,
            "viewport": `${width}x${height}`,
            "deviceType": `${deviceType}`,
            "url": window.location.href
          })
        });
      } catch (error) {
        console.log(`Error posting event for action "${action}":`, error);
      }
    }
  }, [apiAuthKey, deviceType]);

  useEffect(() => {
    if (queryDate && queryDate === initialDate) {
      handleEvent(`Visit by shared link`);
    }
  }, [queryDate, initialDate, handleEvent]);

  useEffect(() => {
    const handleResize = () => {
      setCalendarVisible(false);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    let timerId;
    if (showNew) {
      timerId = setTimeout(() => {
        setShowNew(false);
      }, NEW_BADGE_TIMEOUT);
    }
    return () => clearTimeout(timerId);
  }, [showNew]);

  const fetchData = useCallback(async (date) => {
    setLoaded(false);
    setImageUrl('');
    document.title = `UKArchiveTV: ${date}`; // Use date parameter
    try {
      const response = await fetch(`${config.apiUrl}${date}`, {
        method: 'GET',
        headers: {
          'Authorization': `${apiAuthKey.apiAuthKey}`,
        },
      });
      const data = await response.json();
      const newImageUrl = data.issueUrl ? (data.issueUrl === "missing" ? `${config.missingUrl}` : data.issueUrl) : `${config.missingUrl}`;
      setImageUrl(newImageUrl);
      setIsNote(!!data.notes); // Ensure boolean
      navigate(`/${date}`, { replace: true });
    } catch (error) {
      console.error('Error fetching picUrl:', error);
      setImageUrl(config.missingUrl); // Fallback image on error
    } finally {
      setLoaded(true); // Ensure loaded is set even on error
    }
  }, [apiAuthKey, navigate]); // config.apiUrl, config.missingUrl are stable

useEffect(() => {
  fetchData(selectedDate);
}, [selectedDate]);

  const handleCopyLink = useCallback(() => {
    handleEvent(`Copy link pressed`);
    navigator.clipboard.writeText(`${window.location.origin}/?date=${selectedDate}`)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), ALERT_TIMEOUT);
      })
      .catch((error) => {
        console.error('Failed to copy:', error);
      });
  }, [selectedDate, handleEvent]);

  const handleFeedbackClose = useCallback(() => {
    setShowFeedbackDialog(false);
    // setFeedbackResponseSubmitted(false); // Removed, dialog handles its own state
  }, []);

  const handleNoteClose = useCallback(() => {
    setShowNoteDialog(false);
    // setNoteResponseSubmitted(false); // Removed, dialog handles its own state
  }, []);

  const handleAboutClose = useCallback(() => setShowAboutDialog(false), []);
  const handleDatesClose = useCallback(() => setShowDatesDialog(false), []);
  const handlePrivacyClose = useCallback(() => setShowPrivacyDialog(false), []);


  const handleFeedback = useCallback(() => {
    handleEvent(`Feedback pressed`);
    handleAboutClose(); // As in original
    // handlePrivacyClose(); // Not in original handleFeedback
    setShowFeedbackDialog(true);
  }, [handleEvent, handleAboutClose]);

  const handleAbout = useCallback(() => {
    handleEvent(`About pressed`);
    handlePrivacyClose();
    handleFeedbackClose();
    setShowAboutDialog(true);
  }, [handleEvent, handleAboutClose, handleFeedbackClose]);

  const handleNote = useCallback(() => {
    handleEvent(`Visitor Note pressed`);
    handlePrivacyClose();
    handleFeedbackClose();
    handleAboutClose();
    setShowNoteDialog(true);
  }, [handleEvent, handlePrivacyClose, handleFeedbackClose, handleAboutClose]);

  const handlePrivacy = useCallback(() => { 
    handleEvent(`Privacy pressed`);
    handleAboutClose();
    handleFeedbackClose();
    setShowPrivacyDialog(true);
  }, [handleEvent, handleAboutClose, handleFeedbackClose]);

  // handleLike was defined in original but not used, so omitted for brevity unless needed.
  // const handleLike = useCallback(() => {
  //   handleEvent(`Like pressed`);
  // }, [handleEvent]);

  const handleDates = useCallback(() => {
    handleEvent(`TV History pressed`);
    handleFeedbackClose();
    setShowDatesDialog(true);
  }, [handleEvent, handleFeedbackClose]);


  const getGenomeUrl = useCallback((dateStr) => {
    const date = new Date(dateStr);
    if (date >= new Date("1964-04-18")) return `https://genome.ch.bbc.co.uk/schedules/service_bbc_one_london/${dateStr}`;
    if (date >= new Date("1934-01-08")) return `https://genome.ch.bbc.co.uk/schedules/service_bbc_television_service/${dateStr}`;
    if (date >= new Date("1930-10-12")) return `https://genome.ch.bbc.co.uk/schedules/service_rt_national_programme_london/${dateStr}`;
    if (date >= new Date("1930-03-09")) return `https://genome.ch.bbc.co.uk/schedules/service_rt_national_programme_daventry/${dateStr}`;
    if (date >= new Date("1922-11-14")) return `https://genome.ch.bbc.co.uk/schedules/service_rt_2lo/${dateStr}`;
    return null;
  }, []);

  const handleGenome = useCallback(() => {
    handleEvent(`Genome pressed`); // Added event logging
    const genomeUrl = getGenomeUrl(selectedDate);
    if (genomeUrl) {
      window.open(genomeUrl, "_blank", "noopener,noreferrer");
    } else {
      alert("Selected date is out of BBC Genome's range for Radio Times listings.");
    }
  }, [selectedDate, getGenomeUrl, handleEvent]);

  const toggleMenu = useCallback(() => {
    handleEvent(`toggleMenu pressed`);
    setMenuVisible(v => !v);
  }, [handleEvent]);

  const closeMenu = useCallback(() => setMenuVisible(false), []);

  const toggleDayMenu = useCallback(() => {
    handleEvent(`toggleDayMenu pressed`);
    setDayMenuVisible(v => !v);
  }, [handleEvent]);

  const toggleCalendar = useCallback(() => {
    handleEvent(`Calendar pressed`);
    setCalendarVisible(v => !v);
    if (!calendarVisible) closeMenu(); // Close main menu if opening calendar
  }, [calendarVisible, handleEvent, closeMenu]);

  const handleLinkClick = useCallback((date) => {
    setSelectedDate(date);
    handleDatesClose();
    closeMenu();
  }, [handleDatesClose, closeMenu]);

  // scrollToTop was defined in original but not used, omitted for brevity unless needed.

  // --- moveDate function kept exactly as in the original provided file ---
  const moveDate = useCallback((direction, period) => {
    setLoaded(false);
    let newDate = new Date(selectedDate + ' 18:00:00');
    switch (period) {
      case "day":
        newDate = direction === 'next' ? addDays(newDate, 1) : subDays(newDate, 1);
        break;
      case "week":
        newDate = direction === 'next' ? addWeeks(newDate, 1) : subWeeks(newDate, 1);
        break;
      case "month":
        newDate = direction === 'next' ? addMonths(newDate, 1) : subMonths(newDate, 1);
        break;
      case "year":
        newDate = direction === 'next' ? addYears(newDate, 1) : subYears(newDate, 1);
        break;
      case "decade":
        newDate = direction === 'next' ? addYears(newDate, 10) : subYears(newDate, 10);
        break;
      default:
        break;
    }

    if (!(newDate >= new Date(config.lowerDateBound) && newDate <= new Date(config.upperDateBound))) {
      setShowAlert(true);
      setLoaded(true);      
      setTimeout(() => {
        setShowAlert(false);
       }, ALERT_TIMEOUT); // Use constant
    } else {
      setSelectedDate(newDate.toISOString().split('T')[0]);
    }
    setMenuVisible(false);
  }, [selectedDate]);

  const moveDateSpecial = useCallback((specialMoment) => { // dateArg is selectedDate from call-site
    handleEvent(`${specialMoment} pressed`);
    // CalcMoment uses selectedDate from closure, which is what we want.
    // The first argument to CalcMoment should be a Date object.
    let newCalculatedDate = CalcMoment(new Date(selectedDate + ' 18:00:00'), specialMoment, config.lowerDateBound, config.upperDateBound);
    
    if (!(newCalculatedDate >= new Date(config.lowerDateBound) && newCalculatedDate <= new Date(config.upperDateBound))) {
      setShowAlert(true);
      setLoaded(true);      
      setTimeout(() => {
        setShowAlert(false);
       }, ALERT_TIMEOUT); // Use constant
    } else {
      setSelectedDate(newCalculatedDate.toISOString().split('T')[0]);
    }
    setMenuVisible(false);
  }, [selectedDate, handleEvent]);

  return (
    <div className={`base ${isLandscapeView ? 'landscape' : 'portrait'}`}>
      <div className="header">
        {isMobileView && (
          <div className={`controls-menu ${menuVisible ? 'show-menu' : ''}`}>
            <div className="special-controls-wrapper mobile-grid">
              {renderSpecialControls(moveDateSpecial, selectedDate, isMobileView, handleAbout, handleFeedback, handleGenome, handleDates)}
            </div>
          </div>
        )}
        <DateStrap
          selectedDate={selectedDate}
          handleAbout={handleAbout}
          handleDates={handleDates}
          toggleMenu={toggleMenu}
          smallDateStrap={isMobileView}
          isMobileView={isMobileView}
          showNew={showNew}
          toggleCalendar={toggleCalendar}
          calendarVisible={calendarVisible}
          handleCopyLink={handleCopyLink}
          handleFeedback={handleFeedback}
          handleGenome={handleGenome}
        />

        <CalendarDialog
          calendarVisible={calendarVisible}
          selectedDate={selectedDate}
          setSelectedDate={(date) => { // Keep this inline function or useCallback if complex
            setSelectedDate(date);
            setCalendarVisible(false);
            closeMenu();
          }}
          lowerDateBound={config.lowerDateBound}
          upperDateBound={config.upperDateBound}
        />

        {debug === "true" && (
          <DebugDisplay
            config={config}
            selectedDate={selectedDate}
            imageUrl={imageUrl}
            loaded={loaded}
            showAlert={showAlert}
          />
        )}

        <div className="button-toolbar">
          <div className={`controls-wrapper row ${dayMenuVisible && isLandscapeView ? 'show-menu' : ''}`}>
            {renderDateControls(moveDate, 'Decade', 'decade')}
            {renderDateControls(moveDate, 'Year', 'year')}
            {renderDateControls(moveDate, 'Month', 'month')}
            {renderDateControls(moveDate, 'Week', 'week')}
            {renderDateControls(moveDate, 'Day', 'day')}
            {isLandscapeView && (
              <button type="button" className="day-menu-button" onClick={toggleDayMenu}>
                <FontAwesomeIcon icon={faCaretRight} />
              </button>
            )}
          </div>
          {!isMobileView && (
            <div className={`special-controls-wrapper desktop-grid`}>
              {renderSpecialControls(moveDateSpecial, selectedDate, isMobileView, handleAbout, handleFeedback, handleGenome, handleDates)}
            </div>
          )}
          {!dayMenuVisible && isLandscapeView && (
            <button type="button" className="day-menu-button" onClick={toggleDayMenu}>
              <FontAwesomeIcon icon={faCaretLeft} />
            </button>
          )}
        </div>

        <FeedbackDialog
          showFeedbackDialog={showFeedbackDialog}
          handleFeedbackClose={handleFeedbackClose}
          apiFeedbackUrl={config.apiFeedbackUrl}
          apiAuthKey={apiAuthKey.apiAuthKey}
        />

        <NoteDialog
          showNoteDialog={showNoteDialog}
          handleNoteClose={handleNoteClose}
          isNote={isNote}
          apiNoteUrl={config.apiNoteUrl}
          apiGetNotesUrl={config.apiGetNotesUrl}
          apiAuthKey={apiAuthKey.apiAuthKey}
          selectedDate={selectedDate}
        />

        <AboutDialog
          showAboutDialog={showAboutDialog}
          handleAboutClose={handleAboutClose}
          handlePrivacy={handlePrivacy}
          aboutUrl={config.aboutUrl}
        />

        <PrivacyDialog
          showPrivacyDialog={showPrivacyDialog}
          handlePrivacyClose={handlePrivacyClose}
          privacyUrl={config.privacyUrl}
        />
        

        <TreeView
          showDatesDialog={showDatesDialog}
          handleDatesClose={handleDatesClose}
          onLinkClick={handleLinkClick}
          datesUrl={config.datesUrl}
        />
        <Alert showMessage={showAlert} />
        <Clipboard showMessage={copied} />
      </div>
      <div id="image-container">
        <ImageTransform ref={imageViewerRef}
          imageUrl={imageUrl}
          loaded={loaded}
          loadingFilename={config.loadingFilename}
          setLoaded={setLoaded}
          selectedDate={selectedDate}
          handleEvent={handleEvent}
          minScale={config.minScale}
          maxScale={config.maxScale}
          scaleStep={config.scaleStep}
          isNote={isNote}
          handleNote={handleNote}
        />
      </div>
    </div>
  );
};

export default ImageDisplay;
